import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  ScatterChart, Scatter, ZAxis,
} from 'recharts';
import { useObsCostByArea, useObsEBITDA } from '@/hooks/useObservability';
import { LoaderBlock, fmtUSD, fmtNum } from './shared';

const COLORS = ['#8b5cf6','#3b82f6','#10b981','#f59e0b','#ef4444','#06b6d4','#a855f7','#ec4899'];

export function ObsAreaTab() {
  const { t } = useTranslation();
  const { data: areaRows = [], isLoading } = useObsCostByArea();
  const { data: ebitda = [] } = useObsEBITDA();

  const ytdByArea = useMemo(() => {
    const map = new Map<string, { area: string; usd: number; users: number }>();
    for (const r of areaRows) {
      const cur = map.get(r.area) || { area: r.area, usd: 0, users: 0 };
      cur.usd += r.ai_cost_usd || 0;
      cur.users = Math.max(cur.users, r.active_users || 0);
      map.set(r.area, cur);
    }
    return Array.from(map.values())
      .sort((a, b) => b.usd - a.usd);
  }, [areaRows]);

  if (isLoading) return <LoaderBlock />;

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('observability.area.ytd_title')}</h3>
        <ResponsiveContainer width="100%" height={Math.max(300, ytdByArea.length * 28)}>
          <BarChart data={ytdByArea} layout="vertical" margin={{ left: 130, right: 30 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
            <YAxis type="category" dataKey="area" width={130} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v: any) => fmtUSD(v)} />
            <Bar dataKey="usd" radius={[0, 4, 4, 0]}>
              {ytdByArea.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-1">{t('observability.area.efficiency_title')}</h3>
        <p className="text-xs text-muted-foreground mb-3">
          {t('observability.area.efficiency_note')}
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <ScatterChart margin={{ top: 10, right: 20, bottom: 30, left: 30 }}>
            <CartesianGrid />
            <XAxis type="number" dataKey="users" name="Active users"
                   label={{ value: 'Active users', position: 'bottom' }} />
            <YAxis type="number" dataKey="usd" name="USD"
                   tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`}
                   label={{ value: 'USD', angle: -90, position: 'left' }} />
            <ZAxis range={[100, 100]} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }}
                     formatter={(v: any, n) => n === 'usd' ? fmtUSD(v) : fmtNum(v)}
                     labelFormatter={(_, p: any) => p?.[0]?.payload?.area} />
            <Scatter data={ytdByArea} fill="#8b5cf6" />
          </ScatterChart>
        </ResponsiveContainer>
      </Card>

      <Card className="p-5 overflow-auto">
        <h3 className="text-sm font-semibold mb-3">{t('observability.area.ebitda_title')}</h3>
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground border-b">
            <tr>
              <th className="py-2 pr-3">{t('observability.area.col_month')}</th>
              <th className="pr-3">{t('observability.area.col_area')}</th>
              <th className="pr-3">{t('observability.area.col_vendor')}</th>
              <th className="pr-3">{t('observability.area.col_method')}</th>
              <th className="text-right">{t('observability.area.col_usd')}</th>
            </tr>
          </thead>
          <tbody>
            {ebitda.slice(0, 50).map((r, i) => (
              <tr key={i} className="border-b hover:bg-muted/50">
                <td className="py-1.5 pr-3 text-xs">{r.month.substring(0, 7)}</td>
                <td className="pr-3">{r.area}</td>
                <td className="pr-3">{r.vendor}</td>
                <td className="pr-3">
                  <span className="text-xs px-2 py-0.5 rounded bg-muted">{r.allocation_method}</span>
                </td>
                <td className="text-right font-mono">{fmtUSD(r.ebitda_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {ebitda.length > 50 && (
          <div className="text-xs text-muted-foreground mt-3">
            {t('observability.area.showing_rows', { total: ebitda.length })}
          </div>
        )}
      </Card>
    </div>
  );
}
